// PRE RMA CSB — Enregistrement du service worker (point 32).
export function enregistrerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').then((reg) => { void reg.update().catch(() => undefined); }).catch((err) => {
        console.error('Échec d\'enregistrement du service worker :', err);
      });
    });
  }
}

export type ResultatMiseAJour = 'MISE_A_JOUR_APPLIQUEE' | 'HORS_LIGNE' | 'ERREUR';

/**
 * Icône "Mettre à jour" (en-tête de l'écran Accueil) : garantit que la toute
 * dernière version déployée (UI, design ou logique) s'affiche, sur le web
 * comme dans l'APK (WebView).
 *
 * Étapes :
 *  1. Vérifie que le serveur est joignable AVANT de rien supprimer (sinon, hors
 *     connexion, purger le cache rendrait l'application inutilisable) et
 *     rafraîchit au passage le cache HTTP du navigateur pour "/" et "sw.js".
 *  2. Force la détection d'une nouvelle version du service worker
 *     (registration.update()) et active immédiatement toute version en attente.
 *  3. Purge intégralement le Cache Storage (caches.delete sur toutes les clés).
 *  4. Désinscrit le service worker, puis recharge la page : le rechargement
 *     vient du réseau, et enregistrerServiceWorker() réinstalle un service
 *     worker neuf qui recrée un cache propre.
 *
 * Les données de l'utilisateur (IndexedDB, file d'attente de synchronisation,
 * localStorage) ne sont jamais touchées : seul le Cache Storage est purgé.
 * Conséquence : les lectures API mises en cache pour le hors-ligne sont
 * vidées et se reconstituent à la prochaine consultation en ligne.
 */
export async function verifierMiseAJour(): Promise<ResultatMiseAJour> {
  try {
    // 1. Serveur joignable ? Sinon on ne touche à rien.
    if (typeof navigator.onLine === 'boolean' && !navigator.onLine) return 'HORS_LIGNE';
    try {
      const [shell, sw] = await Promise.all([
        fetch('/', { cache: 'reload' }),
        fetch('/sw.js', { cache: 'reload' }),
      ]);
      if (!shell.ok || !sw.ok) return 'HORS_LIGNE';
    } catch {
      return 'HORS_LIGNE';
    }

    // 2. Détection forcée d'une nouvelle version + activation immédiate.
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        try { await registration.update(); } catch { /* on poursuit : la purge reste utile */ }
        (registration.waiting ?? registration.installing)?.postMessage({ type: 'SKIP_WAITING' });
      }

      // 3. Purge complète du cache local.
      if ('caches' in window) {
        const noms = await caches.keys();
        await Promise.all(noms.map((n) => caches.delete(n)));
      }

      // 4. Désinscription : le prochain chargement ne passe plus par l'ancien worker.
      await Promise.all(registrations.map((r) => r.unregister()));
    } else if ('caches' in window) {
      const noms = await caches.keys();
      await Promise.all(noms.map((n) => caches.delete(n)));
    }

    window.location.reload();
    return 'MISE_A_JOUR_APPLIQUEE';
  } catch (err) {
    console.error('Échec de la mise à jour forcée :', err);
    return 'ERREUR';
  }
}
